import type { ToRefs } from 'vue';
import { toRefs, useHost, watch, onBeforeUnmount } from 'vue';
import { useComponentContext } from 'composables/useComponentContext';
import { createComponentState } from 'types/componentState';
import { useStateStore } from 'composables/useStateStore';
import { unwrap } from 'types/utils';

/**
 * A custom function to store the props of a component in the state store.
 * @returns reactive state object for the current component.
 * @param props
 */
export const useSharedState = <T extends Record<string, any>>(props: Readonly<T>): ToRefs<T> => {
  const stateStore = useStateStore<T>();
  const context = useComponentContext();
  const host = useHost();

  if (context.uid == null) {
    throw new Error(`useSharedState must be used within a component instance.`);
  }

  const key = context.uid.toString();
  const parentKey = context.parentUid;
  const type = context.typeName ?? 'unknown';

  // initialize once
  if (!stateStore.state[key]) {
    stateStore.setState(key, createComponentState<T>(context.uid, type, parentKey, unwrap(props)));
  }

  // reflect CE v-model/update events back into the store
  if (host) {
    const cleanup: Array<() => void> = [];
    for (const propName in unwrap(props)) {
      const evt = `update:${propName}`;
      const handler = (e: Event) => {
        if (!(e instanceof CustomEvent)) return;
        const next = Array.isArray(e.detail) ? e.detail[0] : e.detail;
        stateStore.setPropValue(key, propName as keyof T, next as T[keyof T]);
      };
      host.addEventListener(evt, handler);
      cleanup.push(() => host.removeEventListener(evt, handler));
    }
    onBeforeUnmount(() => {
      for (const fn of cleanup) fn();
    });
  }

  // Keep store in sync with ONLY external prop changes (from parent component)
  // Don't update if the change came from internal state mutations
  let lastPropsSnapshot = JSON.stringify(unwrap(props));

  watch(
    () => props,
    newProps => {
      const currentSnapshot = JSON.stringify(unwrap(newProps));

      // Only update if props actually changed from outside
      if (currentSnapshot !== lastPropsSnapshot) {
        console.log(`[useSharedState] Props changed externally for ${key}, updating state`);
        lastPropsSnapshot = currentSnapshot;
        stateStore.setState(
          key,
          createComponentState<T>(Number(context.uid), type, parentKey, unwrap(newProps))
        );
      }
    },
    { deep: true }
  );

  // return refs to the live object kept in the store
  return toRefs(stateStore.state[key].props);
};
